import React from 'react'
import CSS from 'csstype'

import {
  useInspectorContext,
  useMapPRMarinaRectToCanvasRelativeRect,
} from '../../../hooks/inspector'
import { SketchElement } from '@sketch/inspector'
import { OVERLAYS_Z_INDEX } from '../constants'

import { LayerOverlay as LayerOverlayStyledComponent } from '../FrameDetailInspectorOverlay.styles'
import * as S from './SelectedElementOverlays.styles'

// The corner SVGs are drawn using an outer border which means they are drawn at a 1
// pixel offset.
const CORNER_OFFSET = 1
const SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS = 12

type SelectedElementOverlaysProps = {
  selectedElement: SketchElement | null
}

/**
 * The selection rectangle drawn over the selected element.
 *
 * Only the rectangle: an entity's own chrome - its name, CTA and outline - is
 * drawn by `PageCanvasFramesOverlays` in both modes. This used to render a
 * second `EntityOverlay` for the selected entity, which meant punching a hole
 * in that list (`hiddenEntityUUID`) so the two didn't stack, and meant the
 * selected entity's chrome lived in a container that hides itself while the
 * camera moves - so panning made it disappear.
 */
export function SelectedElementOverlays({
  selectedElement,
}: SelectedElementOverlaysProps) {
  const { sketchSceneRootElement } = useInspectorContext()

  // We don't show artboard as selected element.
  if (!selectedElement || !sketchSceneRootElement) {
    return null
  }

  return <SelectionRectangle selectedElement={selectedElement} />
}

type SelectionRectangleProps = {
  selectedElement: SketchElement
}
function SelectionRectangle({ selectedElement }: SelectionRectangleProps) {
  const bounds = selectedElement.prMarinaNode.getAbsoluteLayerBounds()
  const canvasRelativeRect = useMapPRMarinaRectToCanvasRelativeRect(bounds)

  if (!canvasRelativeRect) {
    return null
  }

  const { x, y, width, height } = canvasRelativeRect
  // Don't show the corners if the selected rectangle is very small
  // as the corner won't fit.
  const showMinimalVersion =
    width < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS ||
    height < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS

  if (showMinimalVersion) {
    return (
      <LayerOverlayStyledComponent
        data-testid="selected-layer-overlay"
        $borderSize={1}
        $color="orange"
        $width={width}
        $height={height}
        $x={x}
        $y={y}
        $zIndex={OVERLAYS_Z_INDEX.SELECTION_RECTANGLE}
      />
    )
  }

  const cornerStyles: CSS.Properties = {
    position: 'absolute',
    width: `6px`,
    height: `6px`,
  }

  return (
    <S.SelectionRectangleContainer
      $width={width}
      $height={height}
      $x={x}
      $y={y}
      data-testid="selected-layer-overlay"
    >
      <S.SelectionRectangleTopEdgeLine />
      <S.SelectionRectangleBottomEdgeLine />
      <S.SelectionRectangleLeftEdgeLine />
      <S.SelectionRectangleRightEdgeLine />

      <S.TopLeftCornerIcon
        style={{
          top: -CORNER_OFFSET,
          left: -CORNER_OFFSET,
          ...cornerStyles,
        }}
      />
      <S.TopRightCornerIcon
        style={{
          top: -CORNER_OFFSET,
          right: -CORNER_OFFSET,
          ...cornerStyles,
        }}
      />
      <S.BottomLeftCornerIcon
        style={{
          bottom: -CORNER_OFFSET,
          left: -CORNER_OFFSET,
          ...cornerStyles,
        }}
      />
      <S.BottomRightCornerIcon
        style={{
          bottom: -CORNER_OFFSET,
          right: -CORNER_OFFSET,
          ...cornerStyles,
        }}
      />
    </S.SelectionRectangleContainer>
  )
}
